import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { use, useEffect, useMemo, useRef, useState } from 'react';

import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { aggregateDisplayPnl, aggregateDisplayPnlPercent } from '@flipster/features/src/shared/pnl/get-display-pnl';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { FormattedMessage } from '@flipster/i18n';
import { openPositionsBunja } from '@flipster/r230522/original/position';
import { Badge } from '@flipster/react/design-system/badge';
import Formatted from '@flipster/react/design-utils/formatted';
import { css, cx } from '@flipster/styled-system/css';
import { flex, stack } from '@flipster/styled-system/patterns';
import { scrollbar } from '@flipster/styled-system/recipes';
import { getChartLinkedTickerPrice } from '@flipster/tvchart/src/misc/chart-price-type';
import { UsdtPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { SymbolScope } from '@web/app/[locale]/(web)/trade/_misc/symbol';
import { TradePipSymbolContext } from '@web/app/[locale]/(web)/trade/_misc/trade-pip/index';
import { symbolPositionCountBunja } from '@web/hooks/trade/use-close-positions';

import TradeLineChart from './TradeLineChart';
import { PointCount, pipChartDataBunja } from './bunja';
import { pipChartPriceType, pipUpnlBasis } from './constants';

const ChartMinHeight = 48;

const ChartTab = () => {
  const symbol = use(TradePipSymbolContext);

  const containerRef = useRef<HTMLDivElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const symbolHeaderRef = useRef<HTMLDivElement>(null);
  const pnlContainerRef = useRef<HTMLDivElement>(null);

  const { openPositionsAtom } = useBunja(openPositionsBunja);
  const openPositions = useAtomValue(openPositionsAtom);
  const symbolPositionCountAtom = useBunja(symbolPositionCountBunja, [SymbolScope.bind(symbol)]);
  const { symbolOpenCount } = useAtomValue(symbolPositionCountAtom);
  const { pipChartDataAtom, getTickerAtom } = useBunja(pipChartDataBunja, [SymbolScope.bind(symbol)]);
  const chartData = useAtomValue(pipChartDataAtom);
  const { instrumentsKeyBySymbol } = useSnapshotSpec();
  const apiSymbol = instrumentsKeyBySymbol?.[symbol];
  const socketSymbol = useAtomValue(getTickerAtom);

  const [chartWidth, setChartWidth] = useState(0);
  const [chartHeight, setChartHeight] = useState(0);

  const symbolPositions = useMemo(() => openPositions?.[symbol] || [], [openPositions, symbol]);

  const unrealizedPnl = useMemo(() => {
    return aggregateDisplayPnl(symbolPositions, { pnlMode: 'unrealizedPnl', upnlBasis: pipUpnlBasis });
  }, [symbolPositions]);

  const unrealizedPnlPct = useMemo(() => {
    return aggregateDisplayPnlPercent(symbolPositions, { pnlMode: 'unrealizedPnl', upnlBasis: pipUpnlBasis });
  }, [symbolPositions]);

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current || !chartContainerRef.current) return;
      const { width: containerWidth, height: containerHeight } = containerRef.current.getBoundingClientRect();
      const { width: chartContainerWidth } = chartContainerRef.current.getBoundingClientRect();
      const { height: symbolHeaderHeight } = symbolHeaderRef.current?.getBoundingClientRect() || { height: 0 };
      const { height: pnlContainerHeight } = pnlContainerRef.current?.getBoundingClientRect() || { height: 0 };
      const Padding = 8;

      setChartWidth(chartContainerWidth);
      setChartHeight(Math.min(containerWidth, containerHeight - symbolHeaderHeight - pnlContainerHeight - Padding));
    };

    if (!containerRef.current) return;
    handleResize();
    const ro = new ResizeObserver(() => {
      handleResize();
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className={cx(
        stack({
          w: '100%',
          h: '100%',
          px: 16,
          overflow: 'scroll',
        }),
        scrollbar(),
      )}
    >
      <div ref={symbolHeaderRef} className={css({ display: 'flex', flexDir: 'column', pt: 12 })}>
        <div>
          <SymbolDisplay
            symbol={symbol}
            icon={<SymbolDisplay.Icon size={20} space={8} />}
            mainLabel={<SymbolDisplay.Label value='shortName' css={{ textStyle: 'Text/L/Strong' }} />}
          />
        </div>
        <div className={css({ display: 'flex', gap: 8 })}>
          <Formatted.Number
            method='round-half-up'
            value={getChartLinkedTickerPrice(socketSymbol, pipChartPriceType)}
            css={{ textStyle: 'Title/XS' }}
            precision={apiSymbol?.pricePrecision}
            toFixed
            withComma
          />
          <Formatted.Percent
            value={socketSymbol?.priceChangePct24h}
            toFixed
            withComma
            withArrow
            withColor
            css={{ textStyle: 'Text/M/Strong' }}
          />
        </div>
      </div>
      <div ref={chartContainerRef} style={{ width: '100%', height: chartHeight, minHeight: ChartMinHeight }}>
        <TradeLineChart
          chartData={chartData}
          maxPointCount={PointCount}
          width={chartWidth}
          height={Math.max(chartHeight, ChartMinHeight)}
          chartMinHeight={ChartMinHeight}
        />
      </div>
      {symbolOpenCount > 0 && (
        <div className={flex({ flexDir: 'column', gap: 4, flex: 1 })}>
          <div ref={pnlContainerRef} className={css({ pt: 8, pb: 16 })}>
            <div className={flex({ color: 'content.default.level-3', textStyle: 'Text/M/Regular', gap: 4 })}>
              <span>
                <FormattedMessage id='Autogen_common.r251126.s_Unrealized_PnL' />
              </span>
              {symbolOpenCount > 1 && (
                <Badge color='neutral'>
                  <FormattedMessage
                    id='Autogen_common.r251126.s_symbolOpenCount_positions'
                    values={{
                      symbolOpenCount: symbolOpenCount,
                    }}
                  />
                </Badge>
              )}
            </div>
            <div className={flex({ textStyle: 'Title/S', gap: 4 })}>
              <Formatted.Number value={unrealizedPnl} precision={UsdtPrecision} toFixed withComma withPlus withColor />
              <Formatted.Percent
                value={unrealizedPnlPct}
                toFixed
                withComma
                withColor={Num(unrealizedPnl).abs().gte('0.01')}
                withPlus
                withParentheses
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartTab;
